import {
  MAXIMUM_REPORT_FILTER_URL_LENGTH,
  MINIMUM_VIDEO_COMPARISON_SELECTIONS,
  encodedLength,
  parseVideoSelectionUrl,
  serializeVideoSelectionUrl,
} from "@meta-video/contracts";

import {
  hasAmbiguousSearchKeys,
  parseVideoWorkspaceSearch,
  removeSearchKeyFamilies,
  removeVideoWorkspaceSearch,
} from "./workspaceSearch.js";

export interface AuxiliarySearchNormalization {
  readonly normalized: boolean;
  readonly search: string;
}

export type ComparisonViewSearchParseResult =
  | Readonly<{ readonly kind: "absent" }>
  | Readonly<{ readonly kind: "valid" }>
  | Readonly<{ readonly kind: "invalid"; readonly message: string }>;

const COMPARISON_VIEW_KEYS = ["compareView"] as const;
const VIDEO_DETAIL_VIEW_KEYS = ["videoView"] as const;
const COMPARISON_KEYS = ["compare", "compareView"] as const;
const AUXILIARY_VIDEO_KEYS = [
  "video",
  "videoView",
  "compare",
  "compareView",
] as const;

export function rawSearch(input: string): string {
  return input.startsWith("?") ? input.slice(1) : input;
}

export function parseComparisonViewSearch(
  input: string,
): ComparisonViewSearchParseResult {
  const params = new URLSearchParams(rawSearch(input));
  if (hasAmbiguousSearchKeys(params, COMPARISON_VIEW_KEYS)) {
    return { kind: "invalid", message: "Comparison view state is ambiguous." };
  }
  const value = params.get("compareView");
  if (value === null) return { kind: "absent" };
  if (value !== "1") {
    return { kind: "invalid", message: "Comparison view state is invalid." };
  }
  return { kind: "valid" };
}

export function removeComparisonView(input: string): string {
  const params = new URLSearchParams(rawSearch(input));
  removeSearchKeyFamilies(params, COMPARISON_VIEW_KEYS);
  return params.toString();
}

export function removeVideoDetailView(input: string): string {
  const params = new URLSearchParams(rawSearch(input));
  removeSearchKeyFamilies(params, VIDEO_DETAIL_VIEW_KEYS);
  return params.toString();
}

export function clearComparisonUrl(input: string): string {
  const params = new URLSearchParams(rawSearch(input));
  removeSearchKeyFamilies(params, COMPARISON_KEYS);
  return params.toString();
}

/**
 * Removes auxiliary UI-owned video state only when it is what pushes the
 * complete workspace query over the shared ceiling. An oversized remainder is
 * returned unchanged so canonical filter parsing continues to fail closed.
 */
export function normalizeOversizedAuxiliarySearch(
  input: string,
): AuxiliarySearchNormalization {
  const search = rawSearch(input);
  if (encodedLength(search) <= MAXIMUM_REPORT_FILTER_URL_LENGTH) {
    return { normalized: false, search };
  }
  const params = new URLSearchParams(search);
  const foundAuxiliaryState = removeSearchKeyFamilies(
    params,
    AUXILIARY_VIDEO_KEYS,
  );
  const normalized = params.toString();
  if (
    !foundAuxiliaryState
    || encodedLength(normalized) > MAXIMUM_REPORT_FILTER_URL_LENGTH
  ) {
    return { normalized: false, search };
  }
  return { normalized: true, search: normalized };
}

/** Prunes composite identities to the permitted brands in one URL rewrite. */
export function pruneVideoUrlState(
  input: string,
  brandIds: readonly string[],
): string {
  const permitted = new Set(brandIds);
  const comparison = parseVideoSelectionUrl(input);
  let nextSearch: string;
  if (comparison.kind === "valid") {
    const retained = comparison.selections.filter(({ brandId }) =>
      permitted.has(brandId));
    nextSearch = retained.length === comparison.selections.length
      ? rawSearch(input)
      : serializeVideoSelectionUrl(retained, input);
    if (retained.length < MINIMUM_VIDEO_COMPARISON_SELECTIONS) {
      nextSearch = removeComparisonView(nextSearch);
    }
  } else if (comparison.kind === "invalid") {
    nextSearch = clearComparisonUrl(input);
  } else {
    nextSearch = removeComparisonView(input);
  }

  const video = parseVideoWorkspaceSearch(nextSearch);
  if (video.kind === "invalid") {
    return removeVideoWorkspaceSearch(nextSearch);
  }
  if (video.video !== null && !permitted.has(video.video.brandId)) {
    return removeVideoWorkspaceSearch(nextSearch);
  }
  return video.videoView ? removeComparisonView(nextSearch) : nextSearch;
}
